import { useTranslation } from 'react-i18next';
import { useMoney } from '../../../lib/money/useMoney.js';
import { Icon } from '../../../components/ui/icon.js';
import type { SceneSelection } from '../scenes/types.js';
import type { DiceBet } from '../types.js';
import { dicePayoutMultiplier, diceSingleTierMultipliers } from './dicePayout.js';

interface Props {
  bet: bigint;
  selection?: SceneSelection;
}

function betDisplayName(bet: DiceBet, t: (k: string) => string): string {
  if (bet === 'low') return t('dice.low');
  if (bet === 'high') return t('dice.high');
  if (bet === 'triple-any') return t('dice.bet.any');
  const m = /^(total|triple|pair|single)-(\d+)$/.exec(bet);
  if (!m) return bet;
  const n = m[2];
  if (m[1] === 'total') return `${t('dice.totals')} ${n}`;
  if (m[1] === 'triple') return `${t('dice.bet.triple')} ${n}·${n}·${n}`;
  if (m[1] === 'pair') return `${t('dice.bet.pair')} ${n}·${n}`;
  return `${t('dice.bet.single')} ${n}`;
}

function payoutMinor(bet: bigint, mult: number): bigint {
  return (bet * BigInt(Math.round(mult * 100))) / 100n;
}

/**
 * "What if you win?" readout for the selected spot — the dice sibling of
 * MinesPayoutPreview. One row for fixed-odds spots; single-N expands into its
 * match-count tiers (1/2/3 dice) with the triple highlighted.
 */
export function DicePayoutPreview({ bet, selection }: Props) {
  const { t } = useTranslation('originals');
  const { fromMinor } = useMoney();
  if (selection?.kind !== 'dice' || !selection.bet) return null;
  const diceBet = selection.bet;

  const rows = /^single-\d+$/.test(diceBet)
    ? diceSingleTierMultipliers().map((mult, i) => ({
        key: `match-${i + 1}`,
        label: t('dice.preview.match', { count: i + 1 }),
        mult,
        top: i === 2,
      }))
    : [{ key: diceBet, label: betDisplayName(diceBet, t), mult: dicePayoutMultiplier(diceBet), top: true }];

  return (
    <div className="mt-4 flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-[var(--text-muted)]">
          {t('dice.preview.title')}
        </span>
        <span className="text-[11px] bf-tnum text-[var(--text-muted)]">
          {t('dice.preview.fromBet', { amount: fromMinor(bet) })}
        </span>
      </div>

      <div className="overflow-hidden rounded-xl bg-[var(--bg-sunken)]">
        {rows.map((r) => (
          <div
            key={r.key}
            className={[
              'flex items-center gap-2.5 px-3 py-2 border-t border-[var(--border-subtle,var(--border))] first:border-t-0',
              r.top && rows.length > 1 ? 'bg-[var(--warning)]/10' : '',
            ].join(' ')}
          >
            <span
              className={[
                'grid h-6 w-6 shrink-0 place-items-center rounded-md',
                r.top && rows.length > 1 ? 'bg-[var(--warning)]/15' : 'bg-[var(--bg-elevated)]',
              ].join(' ')}
            >
              <Icon
                name="dice"
                size={13}
                className={r.top && rows.length > 1 ? 'text-[var(--warning)]' : 'text-[var(--primary)]'}
              />
            </span>
            <span className="min-w-0 truncate text-[12.5px] font-semibold text-[var(--text-secondary)]">
              {r.label}
            </span>
            <span className="ml-auto shrink-0 text-[13px] font-extrabold bf-tnum text-[var(--primary)]">
              ×{r.mult.toFixed(2)}
            </span>
            <span className="w-[92px] shrink-0 text-right text-[12.5px] font-bold bf-tnum text-[var(--success)]">
              {fromMinor(payoutMinor(bet, r.mult))}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
